import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import { MonoLabel } from '../Editorial';
import PowerScoreCard from '../PowerScoreCard';
import { POWER_SCORE_REQUIRED } from '../../lib/powerScoreFields';
import AthleticsHalf from './me/AthleticsHalf';
import PowerScoreStrip from './me/PowerScoreStrip';
import StatGroups from './me/StatGroups';
import FilmSection from './me/FilmSection';
import SeasonStatsGap from './me/SeasonStatsGap';
import './me/me.css';

/**
 * Recruit ▸ Me — the athlete's own record, in the order a coach reads it.
 *
 * Answers two questions in one scroll: what a coach sees when they open you,
 * and what is still blank. The data behind it lives in the profile editors;
 * this surfaces it where recruiting decisions get made and keeps those editors
 * as the write path.
 *
 * Ported from RecruitMeView.swift / RecruitMeScreen.kt.
 * Spec: docs/superpowers/specs/2026-08-10-web-recruit-me-design.md
 */
export default function MePanel() {
  const { API_URL } = useAuth();
  const navigate = useNavigate();
  const [profile, setProfile] = useState(null);
  const [accolades, setAccolades] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showExplainer, setShowExplainer] = useState(false);

  // Power Score is FREE (spec section 2): it is proof the athlete is real, not
  // leverage sold back to them. Do not reintroduce a role-derived lock here —
  // `base_user` on the wire means "no subscription row", NOT "not entitled".

  useEffect(() => {
    let cancelled = false;
    const cfg = { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } };
    (async () => {
      try {
        const res = await axios.get(`${API_URL}/athletes/profile`, cfg);
        if (!cancelled) setProfile(res.data || null);
      } catch (err) {
        console.error('Error loading profile for Recruit Me:', err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    // Accolades live in their own ledger; a failure here just drops the
    // "Accolades" hint from the My Sports subtitle.
    axios.get(`${API_URL}/athletes/accolades`, cfg)
      .then((res) => { if (!cancelled) setAccolades(res.data || []); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [API_URL]);

  if (loading) {
    return <p className="ed-muted" style={{ padding: '8px 0' }}>Loading your record…</p>;
  }

  const measurables = {};
  (profile?.measurables || []).forEach((m) => { measurables[m.key] = m.value; });
  const missingForPowerScore = POWER_SCORE_REQUIRED
    .filter((f) => !measurables[f.name])
    .map((f) => f.label);

  return (
    <>
      <p className="ed-muted me-frame" data-testid="me_frame">
        This is your profile as a coach reads it.
      </p>
      <div className="me-card">
        <AthleticsHalf profile={profile} accolades={accolades} />

        {/* Reads as a seam inside one card, not as two cards. */}
        <div className="me-card__seam" />

        <div className="me-half">
          <div className="me-eyebrow">
            <MonoLabel>What Coaches See</MonoLabel>
            <button
              type="button"
              className="me-eyebrow__action"
              onClick={() => navigate('/measurables')}
              data-testid="recruit_me_measurables_edit"
              aria-label="Edit your measurables"
            >
              Edit
            </button>
          </div>

          <p className="me-lede">
            Grouped the way a coach reads them — size, speed, power, strength.
            Everything here is visible to any coach who opens your profile. Marks
            a staff member confirmed at a camp carry a blue seal — you can&apos;t
            set that yourself.
          </p>

          <PowerScoreStrip score={profile?.power_score} onExplain={() => setShowExplainer((v) => !v)} />

          {showExplainer && (
            <PowerScoreCard score={profile?.power_score} missing={missingForPowerScore} />
          )}

          <StatGroups profile={profile} />
          <FilmSection />
          <SeasonStatsGap profile={profile} />
        </div>
      </div>
    </>
  );
}
